import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import Banner from 'yoda-core-components/lib/components/Banner/Banner';
import {
    fetchChannelInfo,
    checkUrlExtension,
    replaceSubdomain,
} from 'yoda-core-components/lib/helpers/Utils/Utils';
import styles from './Grid.css';
import RichText from '../RichText/RichText';
import Link from '../Link/Link';
import HorizontalSlider from '../HorizontalSlider/HorizontalSlider';
import GridComponent from './GridComponent';

const cx = classNames.bind(styles);

export class Grid extends Component {
    static propTypes = {
        adobeAudience: PropTypes.string,
        irisComponents: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
        numberOfColumns: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        items: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
        gridStyle: PropTypes.oneOfType([PropTypes.array, PropTypes.object, PropTypes.string]),
        hasBorder: PropTypes.bool,
        pageType: PropTypes.string,
        preferences: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
        deviceType: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
        featureFlags: PropTypes.string,
        headline: PropTypes.string,
        description: PropTypes.string,
        seeDetailsLink: PropTypes.object,
        scrollable: PropTypes.string,
        /**
         * Unique name for referencing dom element in automation testing
         * @type {String}
         */
        automationId: PropTypes.string,
        subType: PropTypes.string,
        disclaimer: PropTypes.string,
        primaryLink: PropTypes.object,
        largeMedia: PropTypes.object,
        mediumMedia: PropTypes.object,
        smallMedia: PropTypes.object,
        backgroundColorGrid: PropTypes.string,
    };

    static defaultProps = {
        adobeAudience: '',
        irisComponents: {},
        numberOfColumns: Grid.numberOfCol,
        items: {},
        gridStyle: 'none', // Possible values are comfy, cozy, compact, none
        hasBorder: true,
        pageType: '',
        preferences: {},
        deviceType: {},
        automationId: '',
        featureFlags: '',
        subType: '',
        headline: '',
        description: '',
        seeDetailsLink: {},
        disclaimer: '',
        primaryLink: {},
        largeMedia: {},
        mediumMedia: {},
        smallMedia: {},
        scrollable: '',
        backgroundColorGrid: '',
    };

    constructor() {
        super();
        this.state = {
            setImageHeightFlag: false,
            orientation: false,
        };
        this.imagesHeightList = [];
    }

    componentDidMount() {
        this.handleOrientationChange();
        window.addEventListener('resize', () => this.handleOrientationChange());
    }

    componentWillUnmount() {
        window.removeEventListener('resize', () => this.handleOrientationChange());
    }

    handleOrientationChange = () => {
        const orientation =
            window &&
            window?.orientation &&
            Math.abs(window.orientation) === 90 &&
            window?.innerWidth >= 1280;

        this.setState({ orientation });
    };

    navigateurl = (event, link, targetWindow) => {
        event.preventDefault();
        return {
            url: link,
            openInNewTab: targetWindow,
        };
    };

    onImageLoad = (target) => {
        this.imagesHeightList.push(target?.offsetHeight);
        this.setState((prevState) => ({
            setImageHeightFlag: !prevState.setImageHeightFlag,
        }));
    };

    renderImageHeadline = () => {
        const {
            adobeAudience,
            preferences,
            largeMedia,
            mediumMedia,
            smallMedia,
            deviceType,
            primaryLink: {
                link: { url: primaryLink = '' } = {},
                openInNewTab: openInTabImageLink = false,
            },
        } = this.props;
        const imgTarget = openInTabImageLink ? '_blank' : '';
        const channel = fetchChannelInfo(deviceType);

        const channelSpecificDataMap = {
            desktop: largeMedia,
            tablet: mediumMedia,
            mobile: smallMedia,
        };

        const { altText = '', url = '' } = channelSpecificDataMap[channel];
        const mediaExt = checkUrlExtension(url);
        const convertedUrl = replaceSubdomain(url, preferences);
        const linkWithAudience =
            primaryLink && adobeAudience ? `${primaryLink}-_-${adobeAudience}` : primaryLink;

        return mediaExt === 'mp4' ? (
            <a href={linkWithAudience}>
                <video playsInline autoPlay muted loop width="100%" height="100%">
                    <source
                        src={convertedUrl}
                        type="video/mp4"
                        automationId="gridheadline-banner"
                        target={imgTarget}
                        ariaLabel="banner-image"
                    />
                </video>
            </a>
        ) : (
            url && (
                <div className={cx('imageBlock')}>
                    <Banner
                        automationId="gridheadline-banner"
                        bannerImageClass={cx('imageClass')}
                        bannerClass={cx('imageParentClass')}
                        href={linkWithAudience}
                        bannerImageUrl={convertedUrl}
                        altText={altText}
                        target={imgTarget}
                        ariaLabel="banner-image"
                        onImageLoad={this.onImageLoad}
                    />
                </div>
            )
        );
    };

    renderHeadlineWithUrl = (headline, url, openInNewTab) =>
        url ? (
            <Link
                className={cx('headline')}
                href={url}
                onClick={(event) => this.navigateurl(event, url, openInNewTab)}
            >
                <RichText richTextClass={cx('customHeading')} TagName="h3" text={headline} />
            </Link>
        ) : (
            <RichText richTextClass={cx('customHeading')} TagName="h3" text={headline} />
        );

    renderSeeDetailLink() {
        const {
            adobeAudience,
            deviceType,
            seeDetailsLink: { link: { url = '' } = {}, linkText = '', openInNewTab = false },
        } = this.props;
        const buttonStyleClass =
            deviceType.isTablet || deviceType.isMobile ? 'buttonStyleClass' : null;
        const urlWithAudience = adobeAudience && url ? `${url}-_-${adobeAudience}` : url;

        return (
            url && (
                <Link
                    className={cx('link', buttonStyleClass)}
                    href={urlWithAudience}
                    onClick={(event) => this.navigateurl(event, urlWithAudience, openInNewTab)}
                    type={deviceType.isTablet || deviceType.isMobile ? 'Button' : null}
                >
                    {linkText}
                </Link>
            )
        );
    }

    renderHeading() {
        const {
            adobeAudience,
            description,
            deviceType: { isDesktop = false },
            headline,
            seeDetailsLink: { link: { url = '' } = {}, openInNewTab = false },
        } = this.props;
        const urlWithAudience = adobeAudience && url ? `${url}-_-${adobeAudience}` : url;
        const renderImageHeadline = this.renderImageHeadline();
        const gridHeader =
            headline || description || urlWithAudience || renderImageHeadline ? (
                <div className={cx('grid-heading')}>
                    {headline &&
                        this.renderHeadlineWithUrl(headline, urlWithAudience, openInNewTab)}
                    {renderImageHeadline}
                    {description && (
                        <RichText
                            TagName="h4"
                            richTextClass={cx('customHeading')}
                            text={description}
                        />
                    )}
                    {isDesktop && this.renderSeeDetailLink()}
                </div>
            ) : null;
        return gridHeader;
    }

    renderDislaimer = () => (
        <div className={cx('disclaimerWrapper')}>
            <RichText
                text={this.props.disclaimer}
                richTextClass={cx('disclaimerText')}
                automationId="disclaimerText"
            />
        </div>
    );

    render() {
        const {
            automationId,
            gridStyle,
            disclaimer,
            deviceType,
            pageType,
            preferences,
            featureFlags,
            numberOfColumns,
            description,
            hasBorder,
            irisComponents,
            seeDetailsLink,
            scrollable,
            primaryLink,
            items,
            headline,
            largeMedia,
            mediumMedia,
            smallMedia,
            backgroundColorGrid,
            adobeAudience,
        } = this.props;
        const isScrollable = scrollable === 'scrollable';
        const gridInlineCSS = backgroundColorGrid
            ? { backgroundColor: backgroundColorGrid, margin: 0 }
            : {};
        const gridBgClass = backgroundColorGrid && 'gridBg';
        const isDumbell = this.props.subType && this.props.subType.indexOf('dumbbell') >= 0;
        const gridDumbleWrapperClass = isDumbell ? cx('gridDumble') : cx('gridFullWidth');
        const columnDumble = `gridDumbleCol${Grid.numberOfColumns}`;
        const overflowClass = isScrollable
            ? cx('overflowClass')
            : cx(gridDumbleWrapperClass, columnDumble, `${gridStyle}Wrapper`);
        const gridColumnComp = (
            <GridComponent
                pageType={pageType}
                adobeAudience={adobeAudience}
                deviceType={deviceType}
                preferences={preferences}
                featureFlags={featureFlags}
                numberOfColumns={numberOfColumns}
                items={items}
                gridStyle={gridStyle}
                headline={headline}
                description={description}
                hasBorder={hasBorder}
                automationId={automationId}
                irisComponents={irisComponents}
                seeDetailsLink={seeDetailsLink}
                disclaimer={disclaimer}
                primaryLink={primaryLink}
                largeMedia={largeMedia}
                mediumMedia={mediumMedia}
                smallMedia={smallMedia}
                scrollable={isScrollable}
                onImageLoad={this.onImageLoad}
            />
        );
        const maxHeight = Math.max(...this.imagesHeightList);
        const maxHeightWithPadding = Number.isInteger(maxHeight)
            ? maxHeight + 24
            : null; /* grid slider has 12px padding top and bottom */
        const gridColumns = isScrollable ? (
            <HorizontalSlider
                deviceType={deviceType}
                imageHeight={maxHeightWithPadding}
                key={this.state.orientation}
            >
                {gridColumnComp}
            </HorizontalSlider>
        ) : (
            gridColumnComp
        );
        return (
            <div
                style={gridInlineCSS}
                className={cx('grid-wrapper', overflowClass, gridBgClass)}
                data-automation-id={automationId}
            >
                {this.renderHeading()}
                {gridColumns}
                {(deviceType.isTablet || deviceType.isMobile) && this.renderSeeDetailLink()}
                {disclaimer && this.renderDislaimer()}
            </div>
        );
    }
}

export default Grid;
